import { MoreOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppIconButton from '@nxsys/components/AppIconButton';
import AppIconCard from '@nxsys/components/AppIconCard';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import {
  PAYMENT_MODEL_ENUM,
  getIrelandPaymentModels,
} from '@nxsys/constants/AppConstants';
import { useGetStatisticsTimesheets } from '@nxsys/hooks/api/dashboard';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Icons } from '@nxsys/types/icons';
import { Col, Dropdown, Row } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import ActionRequiredSectionView from './components/ActionRequiredSectionView';
import ExpenseModal from './components/ExpenseModal';
import TimesheetModal, {
  TimesheetModalTypeEnum,
} from './components/TimesheetModal';
import Invoicing from './Invoicing';

const Timesheets = () => {
  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  // Ireland's models depend on the product too: bureau has PAYE + RCT, umbrella
  // adds UMBRELLA. Hardcoding the bureau pair here filtered every Irish
  // umbrella payroll out of this surface's counts and lists.
  const validPaymentModels = isIreland
    ? getIrelandPaymentModels(isBureau)
    : isBureau
      ? [PAYMENT_MODEL_ENUM.PAYEE]
      : [
          PAYMENT_MODEL_ENUM.CIS,
          PAYMENT_MODEL_ENUM.NON_CIS,
          PAYMENT_MODEL_ENUM.UMBRELLA,
          PAYMENT_MODEL_ENUM.PAYEE,
          PAYMENT_MODEL_ENUM.HYBRID,
          PAYMENT_MODEL_ENUM.PAYE_HYBRID,
          PAYMENT_MODEL_ENUM.PAYE_FTH,
        ];
  const { value, loading, run } = useGetStatisticsTimesheets();
  const data = useMemo(() => value?.data, [value]);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [modalType, setModalType] = useState<TimesheetModalTypeEnum | null>(
    null,
  );
  const navigate = useNavigateWithQuery();
  const [isOpenExpenseModal, setIsOpenExpenseModal] = useState(false);

  useEffect(() => {
    run({ paymentModels: validPaymentModels });
  }, []);

  const handleOpenModal = (type: TimesheetModalTypeEnum) => {
    setModalType(type);
    setIsOpenModal(true);
  };

  const cbAfterAction = () => {
    run({ paymentModels: validPaymentModels });
    window.dispatchEvent(new CustomEvent('dashboard:reload-payments'));
    window.dispatchEvent(new CustomEvent('dashboard:reload-clientApprovalReject'));
    window.dispatchEvent(new CustomEvent('dashboard:reload-invoicing'));
  };

  const items = [
    {
      title: data?.timesheets?.totalTimeSheetsApproval,
      description: 'Pending Agency Approval',
      icon: <Icons.IconHomeDollar />,
      onClick: () => handleOpenModal(TimesheetModalTypeEnum.PENDING_APPROVAL),
    },
    {
      title: data?.expense?.totalExpenses,
      description: 'Expense',
      icon: <Icons.IconMoneyBillMinus />,
      onClick: () => setIsOpenExpenseModal(true),
    },
    {
      title: data?.timesheets?.totalPendingDraftGeneration,
      description: 'Generate Draft Payroll',
      icon: <Icons.IconMoneyBillMinus />,
      onClick: () =>
        handleOpenModal(TimesheetModalTypeEnum.GENERATE_DRAFT_PAYROLL),
    },
    {
      title: data?.timesheets?.totalDraftPayroll,
      description: 'Confirm Payroll',
      icon: <Icons.IconMoneyBillCheck />,
      onClick: () => handleOpenModal(TimesheetModalTypeEnum.CONFIRM_PAYROLL),
    },
  ];

  return (
    <>
      <div className="Timesheets">
        <AppCard className="p-[24px]" loading={loading}>
          <div className="flex justify-between items-center mb-[24px]">
            <AppIconCard iconName="IconNoteListCheck" title="Timesheets" />
            <div className="flex items-center gap-x-[8px]">
              <AppButton
                type="primary"
                name="Import"
                leftIcon={
                  <Icons.IconDownload
                    color={defaultTheme.theme.palette.white}
                  />
                }
                onClick={() => navigate('/import/timesheets')}
              />
              <Dropdown
                menu={{
                  items: [
                    {
                      key: 'addPayment',
                      label: 'Add Payment',
                      onClick: () => navigate('/payments'),
                    },
                    {
                      key: 'addDeduction',
                      label: 'Add Deduction',
                      onClick: () => navigate('/deductions'),
                    },
                  ],
                }}
              >
                <AppIconButton icon={<MoreOutlined />} />
              </Dropdown>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-[16px] max-sm:grid-cols-1">
            {items.map((item) => (
              <div
                key={item.description}
                className="border border-[#CDDDD9] dashboard-hover-card rounded-[8px] p-[16px]"
                onClick={item.onClick}
              >
                <div className="bg-warning-100 rounded-[8px] p-[8px] mb-[34px] w-fit">
                  {item.icon}
                </div>
                <div className="text-[22px] font-bold mb-[8px]">
                  {item.title}
                </div>
                <div className="text-[14px] font-bold">{item.description}</div>
              </div>
            ))}
          </div>
        </AppCard>
        {modalType && (
          <TimesheetModal
            isOpen={isOpenModal}
            toggle={() => setIsOpenModal(false)}
            type={modalType}
            cbAfterAction={cbAfterAction}
          />
        )}
        <ExpenseModal
          isOpen={isOpenExpenseModal}
          toggle={() => setIsOpenExpenseModal(false)}
          cbAfterAction={cbAfterAction}
        />
      </div>
      <Row gutter={24}>
        <Col sm={24} md={16}>
          <Invoicing
            totalPendingInvoiceGeneration={
              data?.timesheets?.totalPendingInvoiceGeneration
            }
            isFetchingPendingInvoiceGeneration={loading}
            onClickPendingInvoiceGeneration={() =>
              handleOpenModal(TimesheetModalTypeEnum.PENDING_INVOICE_GENERATION)
            }
          />
        </Col>
        <Col sm={24} md={8}>
          <ActionRequiredSectionView />
        </Col>
      </Row>
    </>
  );
};

export default Timesheets;
